import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '@trim-web-apps/api3map';
import { Form, FormSpecific, Project, Record } from '3map-models';
import { DownloadService } from '@trim-web-apps/core';
import { getMarkerStyleDict } from './map/mapbox.utils';

export const DEFAULT_MARKER_BASE64 =
  'data:image/png;base64,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';

export type QuestionIcon = {
  iconId: string;
  question: string;
};

@Injectable({
  providedIn: 'root',
})
/** @deprecated user MapMarkerService from MapModule */
export class MarkerService {
  // markers: Map<string, ImageBitmap>; // iconId -> base64
  markers: Map<string, ImageBitmap>; // iconId -> base64
  private _ctx: CanvasRenderingContext2D | undefined;
  private _canvas: HTMLCanvasElement;

  constructor(
    private api: ApiService,
    private http: HttpClient,
    private d: DownloadService,
  ) {
    this.markers = new Map();
    this._canvas = document.createElement('canvas');
    const ctx = this._canvas.getContext('2d');
    if (ctx) this._ctx = ctx;
  }

  get ctx(): CanvasRenderingContext2D {
    if (this._ctx) return this._ctx;
    throw Error('Marker manager canvas ctx not found');
  }

  reset(): void {
    this.markers = new Map();
  }

  getDefaultSpecificIdIcon(specificId: string): ImageBitmap | null {
    return this.markers.get(`Default${specificId}`) || null;
  }

  getRecordIcon(record: Record, form: Form): ImageBitmap | null {
    const iconId = getIconId(record, form);
    return this.markers.get(iconId) || null;
  }

  getProjectIconsBase64(project: Project): Observable<boolean> {
    this.markers = new Map();
    const requests = project.formList
      .map((f) => f.specificList)
      .reduce((acc, item) => [...acc, ...item], [])
      .map((specific) => {
        const { questions } = specific;
        const { image, imageNumber, iconFieldType } = specific.markerStyle;

        return this.api.getMarkerBlob(image, project.name).pipe(
          switchMap((blob) => from(this.splitImage(blob, imageNumber))),
          catchError(() => of([] as ImageBitmap[])),
          map((imgBase64List) => {
            const questionIcons = getIconIdListFromSpecific(specific);
            questionIcons.forEach((questIcon, index) => {
              const image = imgBase64List[index] || DEFAULT_MARKER_BASE64;
              this.markers.set(questIcon.iconId, image);
            });
            return true;
          }),
        );
      });

    if (requests.length === 0) return of(true);

    return forkJoin(requests).pipe(
      // delay(3000),
      map((responses) => responses.reduce((acc, item) => acc && item)),
    );
  }

  async splitImage(blob: Blob, splitInto?: number): Promise<ImageBitmap[]> {
    const src = URL.createObjectURL(blob);
    const image: HTMLImageElement = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = src;
    });
    const defaultIconBase64 = DEFAULT_MARKER_BASE64; // await this.getStandardMarkerBase64();
    const imagesNumber = splitInto || Math.floor(image.naturalWidth / 150);
    this.ctx.canvas.width = 150; // image.naturalWidth;
    this.ctx.canvas.height = 150; // image.naturalHeight;
    const bitmapList: ImageBitmap[] = [];
    for (let i = 0; i < imagesNumber; i++) {
      this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
      this.ctx.drawImage(image, i * 150, 0, 150, 150, 0, 0, 150, 150);
      bitmapList.push(await createImageBitmap(this.ctx.canvas));
    }
    return bitmapList;
  }
}

export function getIconId(record: Record, form: Form): string {
  const markerStyleDict = getMarkerStyleDict(form);
  const markerStyle = markerStyleDict[record.formSpecificId];
  const iconFieldType = markerStyle?.iconFieldType;
  let iconId = `Default${record.formSpecificId}`;

  if (iconFieldType) {
    const answerIconFieldType = (record.data as any)[iconFieldType];
    if (answerIconFieldType && answerIconFieldType.type === 'LIST') {
      const value = answerIconFieldType.text || 'Default';
      iconId = `${value}${record.formSpecificId}`;
    }
  }
  return iconId;
}

export function getIconIdListFromSpecific(
  specific: FormSpecific,
): QuestionIcon[] {
  const defaultIcon: QuestionIcon = {
    iconId: `Default${specific.id}`,
    question: 'Default',
  };
  const { iconFieldType } = specific.markerStyle;
  const question = specific.questions.find((q) => q.id === iconFieldType);

  if (!question || question.type !== 'LIST' || !question.options)
    return [defaultIcon];

  const questions: QuestionIcon[] = question.options.map((opt) => {
    return {
      iconId: `${opt}${specific.id}`,
      question: opt,
    };
  });
  return [defaultIcon, ...questions];
}
